import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { differenceInMinutes, toDate } from 'date-fns';

import {
  otherUserJoinedRoomAction,
  otherUserJoinedWaitingRoomAction,
  otherUserLeftEventAction,
  selfJoinedWaitingRoomAction,
  selfLeftEventAction,
  selfMatchedAction,
  selfUnmatchedAction,
} from '../socket/openNatterMessageDispatcher';
import { OpenNatterPhase as Phase } from '../types/state';
import { extractSingleUserFromMap, transformUserMapToParticipants } from '../utils/transformers';
import { connectSocket, socketConnected, socketDisconnected } from './openNatter.actions';
import { natterActions } from './openNatter.actions';
import { markReady } from './openNatter.actions';

import type { OpenNatterState, Participant } from '../types/state';

/**
 * Entity adapters for normalized state
 */
const usersAdapter = createEntityAdapter({
  selectId: (participant: Participant) => participant.id,
});

const matchedAdapter = createEntityAdapter({
  selectId: (participant: Participant) => participant.id,
});

/**
 * Initial state
 */
const initialState: OpenNatterState = {
  phase: Phase.Idle,
  topic: undefined,
  description: undefined,
  videos: [],
  endTime: 0,
  duration: 0,
  startingSoon: undefined,
  serverTimeOffset: 0,
  accessToken: undefined,
  channelName: undefined,
  users: usersAdapter.getInitialState(),
  matched: matchedAdapter.getInitialState(),
  isReadyToBeMatched: false,
  isLoading: false,
  isMarkingReady: false,
  isSocketConnected: false,
  isSocketConnecting: false,
};

/**
 * Helper to calculate duration in minutes
 */
const calculateDuration = (startsAt: string, endsAt: string): number =>
  differenceInMinutes(new Date(endsAt), new Date(startsAt));

/**
 * Open Natter Slice
 */
export const openNatterSlice = createSlice({
  name: 'openNatter',
  initialState,
  reducers: {
    // Reset state to initial (for cleanup on unmount or error)
    resetState: () => initialState,
    // Reset marking ready loading state (for timeout handling)
    markReadyTimeout: (state) => {
      state.isMarkingReady = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // ====================================================================
      // PERSONAL CHANNEL MESSAGES (Sequenced - Strong Consistency)
      // ====================================================================

      .addCase(selfJoinedWaitingRoomAction, (state, action) => {
        // Sequence checking is done in the middleware's sequenceChecker
        const payload = action.payload;

        // === UPDATE OTHER USER PROJECTION ===
        const participants = transformUserMapToParticipants(payload.otherWaitingUsers);

        // Replace all waiting room users with the snapshot from server
        // This ensures a clean slate when joining a new natter
        // Broadcasts that arrived before this will be re-sent by server
        usersAdapter.setAll(state.users, participants);

        // === UPDATE SELF STATE ===
        // Self state is source of truth, only from sequenced Self_ messages
        state.topic = payload.topic;
        state.description = payload.description;
        state.videos = payload.videos;
        state.isReadyToBeMatched = payload.ready;
        state.phase = payload.ready ? Phase.ReadyToMatch : Phase.Waiting;
        state.isLoading = false;
        state.isMarkingReady = false;
      })

      .addCase(selfMatchedAction, (state, action) => {
        // Sequence checking is done in the middleware's sequenceChecker
        const payload = action.payload;

        // === UPDATE OTHER USER PROJECTION ===
        const participants = transformUserMapToParticipants(payload.roomUsers);

        // Clear waiting room users (explicit phase transition)
        usersAdapter.removeAll(state.users);

        // Always merge matched room snapshot with existing state
        matchedAdapter.upsertMany(state.matched, participants);

        // === CALCULATE SERVER TIME OFFSET ===
        // serverTimeOffset = server time - client time (positive if server is ahead)
        const serverNowTime = toDate(payload.serverNow).getTime();
        const clientNowTime = Date.now();
        state.serverTimeOffset = serverNowTime - clientNowTime;

        // === UPDATE SELF STATE ===
        state.topic = payload.topic;
        state.accessToken = payload.videoConferenceDetails.token;
        state.channelName = payload.videoConferenceDetails.channelName;
        state.duration = calculateDuration(payload.startsAt, payload.endsAt);
        // Convert endTime to client time for countdown display
        state.endTime = toDate(payload.endsAt).getTime() - state.serverTimeOffset;
        state.startingSoon = 'matched';
        state.isLoading = false;

        // If startsAt is in the past according to server time, transition immediately
        // Use server time for this check to handle rejoin scenarios accurately
        const startsAtTime = toDate(payload.startsAt).getTime();
        if (startsAtTime <= serverNowTime) {
          state.phase = Phase.Matched;
        }
      })

      .addCase(selfUnmatchedAction, (state, action) => {
        // Sequence checking is done in the middleware's sequenceChecker
        const payload = action.payload;

        // === UPDATE OTHER USER PROJECTION ===
        // Clear waiting room users (explicit phase transition)
        usersAdapter.removeAll(state.users);

        // === CALCULATE SERVER TIME OFFSET ===
        // serverTimeOffset = server time - client time (positive if server is ahead)
        const serverNowTime = toDate(payload.serverNow).getTime();
        const clientNowTime = Date.now();
        state.serverTimeOffset = serverNowTime - clientNowTime;

        // === UPDATE SELF STATE ===
        state.topic = payload.topic;
        state.duration = calculateDuration(payload.startsAt, payload.endsAt);
        // Convert endTime to client time for countdown display
        state.endTime = toDate(payload.endsAt).getTime() - state.serverTimeOffset;
        state.startingSoon = 'unmatched';
        state.isLoading = false;

        // If startsAt is in the past according to server time, transition immediately
        // Use server time for this check to handle rejoin scenarios accurately
        const startsAtTime = toDate(payload.startsAt).getTime();
        if (startsAtTime <= serverNowTime) {
          state.phase = Phase.Unmatched;
        }
      })

      .addCase(selfLeftEventAction, (state) => {
        // Sequence checking is done in the middleware's sequenceChecker
        // === UPDATE SELF STATE ===
        state.phase = Phase.Ended;
        state.isLoading = false;

        // Clear all user projections
        usersAdapter.removeAll(state.users);
        matchedAdapter.removeAll(state.matched);
      })

      // ====================================================================
      // BROADCAST CHANNEL MESSAGES (Unsequenced - Eventual Consistency)
      // IMPORTANT: These ONLY update other user projections, NEVER self state
      // ====================================================================

      .addCase(otherUserJoinedWaitingRoomAction, (state, action) => {
        // Update other user projection (eventually consistent)
        // Allow in Idle phase to handle broadcasts arriving before SelfJoinedWaitingRoom
        if (state.phase !== Phase.Idle && state.phase !== Phase.Waiting && state.phase !== Phase.ReadyToMatch) {
          return;
        }

        const participant = extractSingleUserFromMap(action.payload.userThatJoined);
        usersAdapter.addOne(state.users, participant);
      })

      .addCase(otherUserLeftEventAction, (state, action) => {
        // Update other user projection (eventually consistent)
        const userId = Number(action.payload.userThatLeft.value);

        // Check if we're in a matched room (either fully transitioned or waiting for startsAt)
        const isInMatchedRoom = state.phase === Phase.Matched || state.startingSoon === 'matched';
        // Check if we're in waiting room (including Idle for broadcasts arriving early)
        const isInWaitingRoom =
          state.phase === Phase.Idle || state.phase === Phase.Waiting || state.phase === Phase.ReadyToMatch;

        if (isInMatchedRoom) {
          // Mark as left in matched room
          const existingUser = state.matched.entities[userId];
          if (existingUser) {
            matchedAdapter.updateOne(state.matched, {
              id: userId,
              changes: { leftRoom: true },
            });
          }
        } else if (isInWaitingRoom) {
          // Remove from waiting room
          usersAdapter.removeOne(state.users, userId);
        }
      })

      .addCase(otherUserJoinedRoomAction, (state, action) => {
        // Update other user projection (eventually consistent)
        // When another user joins a room (matched or unmatched), remove them from waiting room
        if (state.phase !== Phase.Waiting && state.phase !== Phase.ReadyToMatch) {
          return;
        }

        // Extract user ID from the userThatJoined map (single user)
        const userIds = Object.keys(action.payload.userThatJoined).map(Number);
        for (const userId of userIds) {
          usersAdapter.removeOne(state.users, userId);
        }
      })

      // ====================================================================
      // JOIN ACTION LIFECYCLE (Loading State)
      // ====================================================================

      .addCase(natterActions.joinNatterClicked.pending, (state) => {
        // Show loading animation when join is clicked
        state.isLoading = true;
      })
      .addCase(natterActions.joinNatterClicked.rejected, (state) => {
        // Hide loading if join fails (timeout or error)
        state.isLoading = false;
      })

      // ====================================================================
      // MARK READY ACTION LIFECYCLE (Loading State)
      // ====================================================================

      .addCase(markReady.pending, (state) => {
        // Show loading animation when mark ready is clicked
        state.isMarkingReady = true;
      })
      .addCase(markReady.rejected, (state) => {
        // Hide loading if mark ready API call fails
        state.isMarkingReady = false;
      })

      // ====================================================================
      // ROOM START DELAY (Wait for startsAt before transitioning phase)
      // ====================================================================

      .addCase(natterActions.userMatched.fulfilled, (state) => {
        // startsAt has been reached, transition to Matched phase
        // Guard: only transition if not already in Matched (prevents race condition)
        if (state.phase !== Phase.Matched) {
          state.phase = Phase.Matched;
        }
      })
      .addCase(natterActions.userUnmatched.fulfilled, (state) => {
        // startsAt has been reached, transition to Unmatched phase
        // Guard: only transition if not already in Unmatched (prevents race condition)
        if (state.phase !== Phase.Unmatched) {
          state.phase = Phase.Unmatched;
        }
      })

      // ====================================================================
      // WEBSOCKET CONNECTION STATE
      // ====================================================================

      .addCase(connectSocket, (state) => {
        // Set connecting state when attempting to connect
        state.isSocketConnecting = true;
      })
      .addCase(socketConnected, (state) => {
        // Socket connected successfully
        state.isSocketConnected = true;
        state.isSocketConnecting = false;
      })
      .addCase(socketDisconnected, (state) => {
        // Socket disconnected
        state.isSocketConnected = false;
        state.isSocketConnecting = false;
      });
  },
});

/**
 * Export reducer and actions
 */
export const { resetState, markReadyTimeout } = openNatterSlice.actions;
export const openNatterReducer = openNatterSlice.reducer;

/**
 * Export entity adapter selectors for use in selectors.ts
 */
export const usersSelectors = usersAdapter.getSelectors();
export const matchedSelectors = matchedAdapter.getSelectors();
